import React, { FC } from 'react';
import {
	extractTextContent,
	getCurrency,
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { UserName } from '../name_component/user/UserNameComponent';
import { showDate, showHour } from '../../utils/DataFormatterUtils';
import { withUserType } from '../withUserType';
import { getFileLink } from '../../utils/FileAccessUtils';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Popover } from 'antd';
import { ArrowRightOutlined, EyeOutlined } from '@ant-design/icons';
import { CURRENCIES } from '../../utils/DataConstants';

function generateLogSummaries(log, workOrder, userType, internalAttachments = []) {
	const {
		fromTitle,
		toTitle,
		fromDescription,
		toDescription,
		fromTaskTitle,
		toTaskTitle,
		changedByContact,
		fromNte,
		toNte,
		fromCurrencyId,
		toCurrencyId,
		fromWoPriority,
		toWoPriority,
		fromProblemType,
		toProblemType,
		fromSupplierFacility,
		toSupplierFacility,
		fromIsCapex,
		toIsCapex,
		fromIsWarranty,
		toIsWarranty,
		fromIsPrivate,
		toIsPrivate,
		messageOverride,
		fromBuyerAttachments,
		toBuyerAttachments,
		fromSupplierAttachments,
		toSupplierAttachments,
		fromSupplierInternalAttachments,
		toSupplierInternalAttachments,
		fromAttachments,
		toAttachments,
	} = log;

	const supplierAttachments = nullSafeGetOrElse('supplierAttachments', workOrder, []);
	const buyerAttachments = nullSafeGetOrElse('buyerAttachments', workOrder, []);
	const supplierInternalAttachments = nullSafeGetOrElse(
		'supplierInternalAttachments',
		workOrder,
		[]
	);

	const currentWOAttachments = [
		...supplierAttachments,
		...buyerAttachments,
		...supplierInternalAttachments,
		...internalAttachments,
	];

	const fromCurrency = CURRENCIES[fromCurrencyId || 'USD'];
	const toCurrency = CURRENCIES[toCurrencyId || 'USD'];

	const isAttachmentRemoved = (att) =>
		!currentWOAttachments.map((_) => _.fileId).includes(att.fileId);

	const changes = [
		{
			from: fromTitle,
			to: toTitle,
			label: 'Title',
		},
		{
			from: fromTaskTitle,
			to: toTaskTitle,
			label: 'Task',
		},
		{
			from: fromDescription,
			to: toDescription,
			label: 'Description',
			format: (val) => extractTextContent(val),
		},
		{
			from: fromNte,
			to: toNte,
			label: 'NTE',
			format: (val, direction) =>
				direction === 'from' ? fromCurrency.format(val) : toCurrency.format(val),
			hideContact: fromCurrencyId !== toCurrencyId,
		},
		{
			from: fromWoPriority && fromWoPriority.name,
			to: toWoPriority && toWoPriority.name,
			label: 'Priority',
		},
		{
			from: fromProblemType && (fromProblemType.hierarchyName || fromProblemType.name),
			to: toProblemType && (toProblemType.hierarchyName || toProblemType.name),
			label: 'Problem Type',
		},
		{
			from: fromSupplierFacility && fromSupplierFacility.name,
			to: toSupplierFacility && toSupplierFacility.name,
			label: 'Supplier',
		},
		{
			from: fromIsCapex,
			to: toIsCapex,
			label: 'Expense Type',
			format: (val) => (val ? 'CapEx' : 'R&M'),
		},
		{
			from: fromIsWarranty,
			to: toIsWarranty,
			label: 'Warranty',
			format: (val) => (val ? 'Under Warranty' : 'Not Under Warranty'),
		},
		{
			from: fromIsPrivate,
			to: toIsPrivate,
			label: 'Work Order Privacy',
			format: (val) => (val ? 'Private' : 'Not Private'),
		},
		{
			from: fromBuyerAttachments,
			to: toBuyerAttachments,
			label: 'Attachments',
			type: 'attachments',
		},
		{
			from: fromSupplierAttachments,
			to: toSupplierAttachments,
			label: 'Attachments',
			type: 'attachments',
		},
		{
			from: fromSupplierInternalAttachments,
			to: toSupplierInternalAttachments,
			label: 'Internal Attachments',
			type: 'attachments',
			internalOnly: true,
		},
		{
			from: fromAttachments,
			to: toAttachments,
			label: 'Internal Attachments',
			type: 'attachments',
			internalOnly: true,
		},
	].map((ch) => ({ ...ch, changedByContact }));

	const getAttachmentChanges = (change) => {
		const oldAttachments = change.from || [];
		const newAttachments = change.to || [];

		const addedAttachments = newAttachments.filter(
			(att) => !oldAttachments.map((_) => _.fileId).includes(att.fileId)
		);
		const removedAttachments = oldAttachments.filter(
			(att) => !newAttachments.map((_) => _.fileId).includes(att.fileId)
		);

		const getLink = (att, disabled = false) => (
			// eslint-disable-next-line jsx-a11y/anchor-is-valid
			<a
				className={`self-center text-base text-gray-400 ${
					disabled ? 'pointer-events-none cursor-not-allowed' : 'underline'
				}`}
				href={disabled ? '#' : getFileLink(att.fileId, att.fileName, userType)}
				target="_blank"
				rel="noreferrer"
			>
				{att.fileName}
			</a>
		);

		const addedAttachmentElements = (addedAttachments || []).map((att) => {
			const disabled = isAttachmentRemoved(att);
			return {
				...change,
				from: undefined,
				prefix: 'Attached',
				to: getLink(att, disabled),
				postfix: disabled ? '(Removed)' : '',
			};
		});

		const removedAttachmentElements = (removedAttachments || []).map((att) => {
			return {
				...change,
				from: undefined,
				prefix: 'Removed',
				to: getLink(att, true),
			};
		});
		return [...addedAttachmentElements, ...removedAttachmentElements];
	};

	for (const change of changes) {
		if (change.from !== undefined || change.to !== undefined) {
			if (change.type === 'attachments') {
				return getAttachmentChanges(change);
			} else {
				return [
					{
						...change,
						messageOverride,
					},
				];
			}
		}
	}

	// Lifecycle events — detected by specific timestamp fields
	const lifecycleLabel = getLifecycleLabel(log);
	if (lifecycleLabel) {
		return [
			{
				from: undefined,
				to: undefined,
				label: lifecycleLabel,
				prefix: lifecycleLabel,
				changedByContact: changedByContact,
				changedByEmail: log.changedBy,
				isLifecycleEvent: true,
			},
		];
	}

	return [];
}

function getLifecycleLabel(log) {
	if (log.createdAt && log.changedAt && log.createdAt === log.changedAt) {
		return 'Work Order Created';
	}
	if (log.markedCompleteAt) {
		return 'Marked Complete';
	}
	if (log.approvedAt) {
		return 'Approved';
	}
	if (log.reopenedAt) {
		return 'Reopened';
	}
	if (log.cancelledAt) {
		return 'Cancelled';
	}
	return null;
}

const InternalOnly: FC<any> = () => {
	return (
		<Popover content="This attachment is hidden from clients and only visible to your team">
			<div className="mx-2 inline-flex items-center gap-1 rounded-full bg-gray-200 px-2.5 py-1 text-sm font-medium text-gray-700">
				<div>
					<EyeOutlined />
				</div>
				<div>Internal Only</div>
			</div>
		</Popover>
	);
};

const EllipsiedSpan = ({ value }) => (
	<span className="inline-block min-w-0 max-w-60 truncate" title={value}>
		{value}
	</span>
);

const ContactOrEmail: FC<any> = ({ contact, email }) => {
	if (contact) {
		return (
			<UserName mode="inline" contact={contact} underline={false} showAvatar showNameWithAvatar />
		);
	}
	if (email) {
		return <span className="text-gray-500">{email}</span>;
	}
	return null;
};

const EachSummary = ({ log, change, compact = false }) => {
	const {
		from,
		to,
		messageOverride,
		changedByContact,
		changedByEmail,
		prefix: existingPrefix,
		label,
		postfix,
		format,
		internalOnly,
		hideContact,
		isLifecycleEvent,
	} = change;

	if (isLifecycleEvent) {
		return (
			<div style={{ maxWidth: '80%' }} className="mx-auto flex flex-col justify-center text-center">
				<div className="flex flex-row flex-wrap items-center justify-center">
					<span className="font-medium">{existingPrefix}</span>
					<div className="ml-2 flex flex-row items-center">
						<ContactOrEmail contact={changedByContact} email={changedByEmail} />
						<span className="mx-2 text-2xl text-gray-500">&middot;</span>
					</div>
					<div className="text-gray-400">
						{showDate(log.changedAt, 'MMM DD')} {showHour(log.changedAt, 'MMM DD')}
					</div>
				</div>
			</div>
		);
	}

	const fromOnlyAvailable = from !== undefined && from !== '';
	const toOnlyAvailalbe = to !== undefined && to !== '';
	const bothFromAndToAvailable = fromOnlyAvailable && toOnlyAvailalbe;

	const prefix =
		existingPrefix ||
		(bothFromAndToAvailable
			? `${label} Change`
			: toOnlyAvailalbe
			? `Set ${label}`
			: fromOnlyAvailable
			? `Removed ${label}`
			: '');

	const formattedFrom = format ? format(from, 'from') : from;
	const formattedTo = format ? format(to, 'to') : to;

	const value = bothFromAndToAvailable ? (
		<span className="flex flex-row items-center">
			<EllipsiedSpan value={formattedFrom} />
			<ArrowRightOutlined className="mx-2" />
			<EllipsiedSpan value={formattedTo} />
		</span>
	) : from ? (
		formattedFrom
	) : to ? (
		formattedTo
	) : null;

	const row1 = messageOverride ? (
		<div>
			{hideContact ? null : (
				<>
					<UserName mode="inline" contact={changedByContact} />{' '}
				</>
			)}
			<span className="text-gray-400">
				{nullSafeGet('message', messageOverride)}
				{nullSafeGet('note', messageOverride) ? (
					<span className={'italic'}>because "{nullSafeGet('note', messageOverride)}"</span>
				) : null}
			</span>
		</div>
	) : null;

	if (compact) {
		return (
			<div className="mx-auto flex max-w-[90%] flex-col items-center justify-center py-2 text-center">
				{row1 || null}
				<div className="flex flex-row flex-wrap items-center justify-center gap-1">
					<span>
						{prefix}
						{':'}{' '}
					</span>
					<span className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-gray-400">
						{value}
					</span>
					{postfix ? <span className="text-sm text-gray-400">{postfix}</span> : null}
				</div>
				<div className="mt-1 flex flex-row items-center justify-center">
					<ContactOrEmail contact={changedByContact} email={changedByEmail} />
					<span className="mx-2 text-2xl text-gray-500">&middot;</span>
					<span className="text-gray-400">
						{showDate(log.changedAt, 'MMM DD')} {showHour(log.changedAt, 'MMM DD')}
					</span>
				</div>
			</div>
		);
	}

	return (
		<div style={{ maxWidth: '80%' }} className="mx-auto flex flex-col justify-center text-center">
			{row1 || null}
			<div className="flex flex-row items-center justify-center">
				{internalOnly ? <InternalOnly /> : null}
				<div className="flex flex-row items-baseline justify-center">
					<div>
						{prefix}
						{':'}
					</div>
					<div
						title={value}
						className="ml-2 max-w-[400px] overflow-hidden text-ellipsis whitespace-nowrap text-gray-400"
					>
						{value}
					</div>
					{postfix ? <div className="ml-2 text-sm text-gray-400">{postfix}</div> : null}
					{hideContact ? (
						<div className="flex flex-row items-center">
							<span className="relative top-1 mx-2 text-2xl text-gray-500">·</span>
						</div>
					) : (
						<div className="ml-2 flex flex-row items-center">
							<div>--</div>
							<div className="ml-2">
								<ContactOrEmail contact={changedByContact} email={changedByEmail} />
							</div>
							<span className="mx-2 text-2xl text-gray-500">·</span>
						</div>
					)}

					<div className="text-gray-400" style={{}}>
						{showDate(log.changedAt, 'MMM DD')} {showHour(log.changedAt, 'MMM DD')}
					</div>
				</div>
			</div>
		</div>
	);
};

const MessageLog: FC<any> = ({
	workOrder,
	userType,
	log,
	compact = false,
	internalAttachments = [],
}): React.ReactElement => {
	const logSummary = generateLogSummaries(log, workOrder, userType, internalAttachments);
	if (!logSummary) return null;
	return (
		<>
			{logSummary.map((ls, idx) => (
				<EachSummary key={`${log.changedAt}-${idx}`} log={log} change={ls} compact={compact} />
			))}
		</>
	);
};

const mapStateToProps = (state, ownProps) => ({
	workOrder: getEntityById(ownProps.match.params.id, state.work_orders),
});

export default withRouter(withUserType(connect(mapStateToProps)(MessageLog)));
